import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { getBaseUrl } from "../config";
import { getAuthApiHeaders } from "../../utils/common";
import { showSnackbar } from "./snackbar";
import secureLocalStorage from "react-secure-storage";

const baseUrl = getBaseUrl();

export const getUserChats = createAsyncThunk(
  "getUserChats",
  async (payload, { rejectWithValue, dispatch }) => {
    try {
      const response = await axios.get(
        `${baseUrl}/sessions/${payload.sessionId}/chats`,
        {
          headers: {
            "Content-Type": "application/json",
            // keep old headers as-is
            "use-case": "src",
            ...getAuthApiHeaders({ includeIncomingEmail: true }),
            client: payload.client ? payload.client : "UT",
          },
          withCredentials: true,
        },
      );

      const data = response.data;

      // ✅ NEW SHAPE: { session: {...}, chats: [...] }
      if (data && Array.isArray(data.chats)) {
        // Normalize to what old UI likely expects: an array of messages
        return data.chats.map((c) => {
          const chart = c.assistant_chart || {};
          const base64 = (chart.base64_image || "").trim();

          const hasChart = !!base64 || chart.chart_intent === true;

          return {
            chat_id: c.chat_id,
            session_id: c.session_id,
            user_session_id: c.session_id,

            human: c.human_text ?? "",
            ai: c.assistant_text ?? "",
            assistant_format: c.assistant_format ?? "text",

            timestamp_created: c.created_at,
            timestamp_modified: c.updated_at,

            human_text: c.human_text ?? "",
            assistant_text: c.assistant_text ?? "",
            ai_metadata:
              c.ai_metadata &&
              typeof c.ai_metadata === "object" &&
              !Array.isArray(c.ai_metadata)
                ? c.ai_metadata
                : {},
            feedback: c.feedback ?? null,
            rating: c.rating ?? null,
            feedback_text: c.feedback_text ?? "",
            feedback_option: c.feedback_option ?? "",
            sources: Array.isArray(c.ai_metadata?.sources)
              ? c.ai_metadata.sources
              : [],

            // ✅ chart / image (NEW)
            chartIntent: hasChart,
            imgPath: base64,

            // (optional) keep metadata if you want later
            chartMeta: chart.metadata || null,
            chartType: chart.chart_type || null,
          };
        });
      }

      // ✅ OLD SHAPE fallback: if API ever returns array directly
      if (Array.isArray(data)) return data;

      // Otherwise return empty list so UI doesn't crash
      return [];
    } catch (error) {
      dispatch(showSnackbar({ message: "Failed to load!", severity: "error" }));
      return rejectWithValue(
        error?.response?.data?.message ||
          error?.response?.statusText ||
          "Request failed",
      );
    }
  },
);

const userChatsSlice = createSlice({
  name: "user-chats",
  initialState: {
    isLoading: false,
    userChats: [],
    isError: false,
    error: null,
    success: false,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(getUserChats.pending, (state) => {
      state.isLoading = true;
      state.error = null;
      state.isError = false;
      state.success = false;
    });
    builder.addCase(getUserChats.fulfilled, (state, action) => {
      state.isLoading = false;
      state.userChats = action.payload; // ✅ always an array now
      state.success = true;
    });
    builder.addCase(getUserChats.rejected, (state, action) => {
      state.isLoading = false;
      state.isError = true;
      state.error = action.payload;
      state.success = false;
    });
  },
});

export default userChatsSlice.reducer;
